import styled from 'styled-components';

import ProgramStatusIcons from './ProgramStatusIcons';
import { isSwitchActive, sortMachineIds } from './zoneDetailUtils';
import MonitoredSwitchCard from './MonitoredSwitchCard';
import SitePlanUpload from './SitePlanUpload';


const ZoneDetailPanel = ({ swtName, locationId, locationData, machineIds, machineMap, isF, onSelectMachine, onBack }) => {
  const sortedMachineIds = sortMachineIds(machineIds, machineMap);
  const machineCount = sortedMachineIds.length;
  const activeCount = sortedMachineIds.filter((machineId) =>
    isSwitchActive(machineMap?.[machineId])
  ).length;

  const zoneId = String(locationData?.zoneId || locationData?.zone_id || locationId || '--');
  const companyName = locationData?.company_name || locationData?.companyName || 'No company assigned';
  const zoneName =
    locationData?.specificLocationName ||
    locationData?.specific_location_name ||
    locationData?.subLocationName ||
    locationData?.sub_location_name ||
    locationData?.specific_address ||
    locationData?.location_name ||
    locationData?.location_name_short ||
    locationData?.locationName ||
    locationData?.zone_name ||
    zoneId;
  const shortName = locationData?.location_name_short || locationData?.zone_name_short || '';
  const streetAddress =
    locationData?.locationAddress ||
    locationData?.zone_address ||
    locationData?.specific_address ||
    locationData?.address?.formatted ||
    '';
  const cityState = [
    locationData?.zone_city,
    locationData?.zone_state_short_name || locationData?.zone_state,
  ]
    .filter(Boolean)
    .join(', ');
  const shortZoneId = zoneId.length > 10 ? zoneId.slice(0, 8).toUpperCase() : zoneId.toUpperCase();

  return (
    <PanelSurface>
      {onBack && (
        <BackButton type="button" onClick={onBack}>
          ← Back to sub-locations
        </BackButton>
      )}
      <TopBar>
        <TopLeft>
          <BadgeRow>
            <ZoneBadge>{`Zone ID: ${shortZoneId}`}</ZoneBadge>
            <CompanyBadge>{companyName}</CompanyBadge>
          </BadgeRow>

          <ZoneName>{zoneName}</ZoneName>
          {(shortName || streetAddress || cityState) && (
            <ZoneMeta>
              {shortName && <span>{shortName}</span>}
              {streetAddress && <span>{streetAddress}</span>}
              {cityState && <span>{cityState}</span>}
            </ZoneMeta>
          )}


        </TopLeft>

        <SitePlanUpload swtName={swtName} locationId={locationId} locationData={locationData} />
      </TopBar>

      <StatsRow>
        <StatCell>
          <StatLabel>Total SWITCHES</StatLabel>
          <StatValue>{machineCount}</StatValue>
        </StatCell>
        <StatCell>
          <StatLabel>Active SWITCHES</StatLabel>
          <StatValue>{activeCount}</StatValue>
        </StatCell>
        <ProgramStatusIcons swtName={swtName} machineMap={machineMap} />
      </StatsRow>

      <SwitchesSection>
        <SectionLabel>Monitored Switches & Sensors</SectionLabel>

        {sortedMachineIds.length ? (
          <CardsRail>
            {sortedMachineIds.map((machineId) => (
              <MonitoredSwitchCard
                key={machineId}
                swtName={swtName}
                machineId={machineId}
                machineData={machineMap?.[machineId]}
                isF={isF}
                onSelect={onSelectMachine}
              />
            ))}
          </CardsRail>
        ) : (
          <EmptyState>
            <EmptyTitle>No monitored switches found</EmptyTitle>
            <EmptyText>This zone is available, but there are no controllers mapped yet.</EmptyText>
          </EmptyState>
        )}
      </SwitchesSection>
    </PanelSurface>
  );
};

export default ZoneDetailPanel;



const BackButton = styled.button`
  align-self: flex-start;
  padding: 6px 14px;
  border: none;
  border-radius: 8px;
  background: var(--switch-blue-soft);
  color: #60a5fa;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: var(--switch-blue-subtle);
  }
`;

const PanelSurface = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 32px;
  padding-bottom: 0.8px;
  background: var(--switch-header-bg);
  border-bottom: 0.8px solid var(--switch-border);
  box-shadow: var(--switch-shadow-soft);
  overflow: hidden;

  @media (max-width: 720px) {
    padding: 16px;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const TopLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ZoneBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 19px;
  padding: 0 8px;
  border-radius: 6px;
  background: rgba(43, 127, 255, 0.1);
  color: #51a2ff;
  font-family: 'Consolas', monospace;
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 1px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const CompanyBadge = styled.span`
  display: inline-flex;
  align-items: center;
  height: 19px;
  padding: 0 8px;
  border-radius: 6px;
  background: var(--switch-panel-bg-alt);
  color: var(--switch-text-faint);
  font-family: 'Consolas', monospace;
  font-size: 10px;
  font-weight: 400;
  letter-spacing: 1px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const ZoneName = styled.h2`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  line-height: 32px;
  letter-spacing: -0.6px;
  color: var(--switch-text-primary);
`;

const ZoneMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  color: var(--switch-text-muted);
  font-size: 12px;
  line-height: 18px;

  span:not(:last-child)::after {
    content: '/';
    margin-left: 8px;
    color: var(--switch-text-faint);
  }
`;


const StatsRow = styled.div`
  display: flex;
  gap: 22px;
  #flex-wrap: wrap;
`;

const StatCell = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 180px;
  height: 81px;
  padding: 16.8px;
  padding-bottom: 0.8px;
  background: var(--switch-panel-bg);
  border: 0.8px solid rgba(59, 130, 246, 0.5);
  border-radius: 14px;
  box-shadow: var(--switch-shadow-soft);
  flex-shrink: 0;
  text-align: center;
  &:nth-child(2) {
    width: 190px;
  }
`;

const StatLabel = styled.span`
  color: var(--switch-text-faint);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  text-align: center;
`;

const StatValue = styled.span`
  color: #51a2ff;
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: -0.5px;
`;

const SwitchesSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 0;
`;

const SectionLabel = styled.h3`
  margin: 0;
  color: var(--switch-text-faint);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.3px;
  text-transform: uppercase;
`;

const CardsRail = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  padding-bottom: 6px;
  flex-wrap: wrap;
  &::-webkit-scrollbar {
    height: 5px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--switch-scrollbar-thumb);
    border-radius: 999px;
  }

  @media (max-width: 720px) {
    flex-direction: column;
    overflow-x: visible;
  }
`;

const EmptyState = styled.div`
  padding: 22px 18px;
  border-radius: 18px;
  border: 1px dashed var(--switch-scrollbar-thumb);
  background: var(--switch-empty-bg);
`;

const EmptyTitle = styled.span`
  display: block;
  color: var(--switch-text-primary);
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 6px;
`;

const EmptyText = styled.span`
  color: var(--switch-text-faint);
  font-size: 12px;
  line-height: 1.5;
`;
